import { useState } from "react";
import axiosInstance from "@/hooks/axios";
import { isAxiosError } from "axios";
import { SavedJobPost } from "@/types/user";
import { JobPost } from "@/types/jobPost";
import { Company } from "@/types/company";

export interface UpdateSavedJobData {
  job_post_id: SavedJobPost["id"];
  application_status?: SavedJobPost["application_status"];
  application_status_comment?: SavedJobPost["application_status_comment"];
  number_of_experienced_years?: SavedJobPost["number_of_experienced_years"];
  saved_reason?: SavedJobPost["saved_reason"];
  notes?: SavedJobPost["notes"];
}

export interface UpdateSavedJobResponse {
  success: boolean;
  message: string;
  saved_job: {
    id: SavedJobPost["id"];
    job_title: JobPost["job_title"];
    company_id: Company["id"];
    application_status: SavedJobPost["application_status"];
    application_status_comment: SavedJobPost["application_status_comment"];
    number_of_experienced_years: SavedJobPost["number_of_experienced_years"];
    saved_reason: SavedJobPost["saved_reason"];
    notes: SavedJobPost["notes"];
    updated_at: SavedJobPost["updated_at"];
  };
}

export const useUpdateSavedJob = () => {
  const [data, setData] = useState<UpdateSavedJobResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const updateSavedJob = async (updateData: UpdateSavedJobData) => {
    setLoading(true);
    setError(null);

    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      setError("Not authenticated.");
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.put<UpdateSavedJobResponse>(
        `users/users/update_saved_job/`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      setData(response.data);
      return response.data;
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, updateSavedJob };
};
